import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import "./styles/Order.css";

const ORDER_API = "https://okna.co.tz/mrms/Orders.php";
const MENU_API = "https://okna.co.tz/mrms/Menu.php";
const ITEMS_PER_PAGE = 7;

export default function Orders() {
    const token = localStorage.getItem("token");
    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [dateFilter, setDateFilter] = useState({
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        filterType: 'today' // 'today', 'yesterday', 'thisWeek', 'custom'
    });

    useEffect(() => {
        if (!token) {
            Swal.fire("Session Expired", "Please login again", "warning");
            return;
        }
        loadAllData();
    }, [token]);

    useEffect(() => {
        filterOrdersByDate();
    }, [orders, dateFilter, currentPage]);

    const loadAllData = async () => {
        setLoading(true);
        try {
            // Add token to URL as fallback
            const url = `${ORDER_API}?admin=true&token=${token}`;
            console.log("Fetching orders from:", url);
            
            const orderRes = await fetch(url, {
                method: "GET",
                headers: { 
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                }
            });
            
            console.log("Response status:", orderRes.status);
            
            // Check for 401 Unauthorized
            if (orderRes.status === 401) {
                Swal.fire({
                    title: "Session Expired",
                    text: "Please login again",
                    icon: "warning",
                    confirmButtonText: "OK"
                }).then(() => {
                    localStorage.removeItem("token");
                    window.location.href = "/login";
                });
                return;
            }
            
            const orderData = await orderRes.json();
            console.log("Orders data:", orderData);
            
            if (Array.isArray(orderData)) {
                const ordersWithItems = await Promise.all(
                    orderData.map(async (order) => {
                        try {
                            // Add token to detail request URL
                            const detailUrl = `${ORDER_API}?id=${order.id}&token=${token}`;
                            const detailRes = await fetch(detailUrl, {
                                headers: { "Authorization": `Bearer ${token}` }
                            });
                            
                            if (detailRes.status === 401) {
                                throw new Error("Authentication failed");
                            }
                            
                            const detail = await detailRes.json();
                            
                            return {
                                id: order.id,
                                order_number: order.order_number,
                                total_amount: order.total_amount || 0,
                                discount_amount: order.discount_amount || 0,
                                created_at: order.created_at,
                                staff_name: order.staff_name || "Unknown",
                                items: detail.items || [],
                                item_count: detail.items?.length || 0
                            };
                        } catch (err) {
                            console.error(`Error fetching order ${order.id}:`, err);
                            return { 
                                ...order, 
                                items: [], 
                                item_count: 0 
                            };
                        }
                    })
                );
                setOrders(ordersWithItems);
                console.log("Orders loaded:", ordersWithItems.length);
            } else {
                console.error("Invalid response:", orderData);
                Swal.fire("Error", orderData.error || "Failed to load orders", "error");
            }
        } catch (err) {
            console.error("Load error:", err);
            Swal.fire("Error", "Server not reachable. Please check your connection.", "error");
        } finally {
            setLoading(false);
        }
    };

    const filterOrdersByDate = () => {
        let filtered = [...orders];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        switch(dateFilter.filterType) {
            case 'today':
                filtered = orders.filter(order => {
                    const orderDate = new Date(order.created_at);
                    orderDate.setHours(0, 0, 0, 0);
                    return orderDate.getTime() === today.getTime();
                });
                break;
            case 'yesterday':
                const yesterday = new Date(today);
                yesterday.setDate(yesterday.getDate() - 1);
                filtered = orders.filter(order => {
                    const orderDate = new Date(order.created_at);
                    orderDate.setHours(0, 0, 0, 0);
                    return orderDate.getTime() === yesterday.getTime();
                });
                break;
            case 'thisWeek':
                const weekStart = new Date(today);
                weekStart.setDate(weekStart.getDate() - weekStart.getDay());
                const weekEnd = new Date(weekStart);
                weekEnd.setDate(weekEnd.getDate() + 6);
                weekEnd.setHours(23, 59, 59, 999);
                filtered = orders.filter(order => {
                    const orderDate = new Date(order.created_at);
                    return orderDate >= weekStart && orderDate <= weekEnd;
                });
                break;
            case 'custom':
                if (dateFilter.startDate && dateFilter.endDate) {
                    const start = new Date(dateFilter.startDate);
                    start.setHours(0, 0, 0, 0);
                    const end = new Date(dateFilter.endDate);
                    end.setHours(23, 59, 59, 999);
                    filtered = orders.filter(order => {
                        const orderDate = new Date(order.created_at);
                        return orderDate >= start && orderDate <= end;
                    });
                }
                break;
            default:
                filtered = orders;
        }

        // Sort by date (newest first)
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setFilteredOrders(filtered);
        setCurrentPage(1);
    };

    const handleFilterChange = (type) => {
        setDateFilter(prev => ({ ...prev, filterType: type }));
    };

    const viewOrder = (order) => {
        setSelectedOrder(order);
        setShowModal(true);
    };

    const deleteOrder = async (order) => {
        const confirm = await Swal.fire({
            title: "Delete Order?",
            text: `Order #${order.order_number}`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!"
        });

        if (!confirm.isConfirmed) return;

        try {
            const url = `${ORDER_API}?id=${order.id}&token=${token}`;
            const res = await fetch(url, {
                method: "DELETE",
                headers: { 
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                }
            });
            
            if (res.status === 401) {
                Swal.fire("Session Expired", "Please login again", "warning");
                localStorage.removeItem("token");
                window.location.href = "/login";
                return;
            }
            
            const data = await res.json();

            if (data.success || data.message) {
                setOrders(prev => prev.filter(o => o.id !== order.id));
                Swal.fire("Deleted!", data.message || "Order deleted successfully", "success");
            } else {
                Swal.fire("Error", data.error || "Failed to delete order", "error");
            }
        } catch (err) {
            console.error("Delete error:", err);
            Swal.fire("Error", "Server error. Please try again.", "error");
        }
    };

    const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginatedOrders = filteredOrders.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const formatCurrency = (amount) => {
        return Number(amount || 0).toLocaleString() + ' TZS';
    };

    const calculateTotals = () => {
        const totalAmount = filteredOrders.reduce((sum, order) => sum + (Number(order.total_amount) || 0), 0);
        const totalDiscount = filteredOrders.reduce((sum, order) => sum + (Number(order.discount_amount) || 0), 0);
        const totalFinal = totalAmount - totalDiscount;
        return { totalAmount, totalDiscount, totalFinal };
    };

    const totals = calculateTotals();

    const filterStyles = {
        container: {
            background: 'linear-gradient(135deg, #033349 0%, #556b04 100%)',
            padding: '20px',
            borderRadius: '15px',
            marginBottom: '20px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
        },
        title: {
            color: 'white',
            margin: '0 0 15px 0',
            fontSize: '1.2rem',
            fontWeight: '600'
        },
        buttonGroup: {
            display: 'flex',
            gap: '10px',
            flexWrap: 'wrap',
            marginBottom: '15px'
        },
        filterButton: (isActive) => ({
            padding: '10px 20px',
            border: 'none',
            borderRadius: '25px',
            fontWeight: '600',
            cursor: 'pointer',
            background: isActive ? 'white' : 'rgba(255,255,255,0.2)',
            color: isActive ? '#764ba2' : 'white',
            transition: 'all 0.3s ease',
            boxShadow: isActive ? '0 5px 15px rgba(0,0,0,0.2)' : 'none'
        }),
        dateRange: {
            display: 'flex',
            gap: '15px',
            alignItems: 'center',
            flexWrap: 'wrap',
            background: 'rgba(255,255,255,0.1)',
            padding: '15px',
            borderRadius: '10px'
        },
        dateInput: {
            padding: '8px 15px',
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: '8px',
            background: 'white',
            color: '#333',
            fontSize: '0.9rem'
        },
        summary: {
            background: 'rgba(255,255,255,0.95)',
            borderRadius: '15px',
            padding: '15px',
            marginTop: '15px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '15px'
        },
        summaryItem: {
            textAlign: 'center',
            padding: '10px',
            borderRight: '1px solid #e5e7eb'
        },
        summaryLabel: {
            fontSize: '0.9rem',
            color: '#6b7280',
            marginBottom: '5px'
        },
        summaryValue: {
            fontSize: '1.4rem',
            fontWeight: '700',
            color: '#374151'
        },
        summaryValueHighlight: {
            fontSize: '1.4rem',
            fontWeight: '700',
            color: '#059669'
        }
    };

    if (loading) {
        return (
            <div className="om-container">
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    gap: '20px'
                }}>
                    <div className="spinner" style={{
                        width: '50px',
                        height: '50px',
                        border: '3px solid #f3f3f3',
                        borderTop: '3px solid #667eea',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }}></div>
                    <p style={{ color: '#6b7280', fontSize: '1.1rem' }}>Loading orders...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="om-container">
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                .filter-button:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 5px 15px rgba(0,0,0,0.2);
                }
            `}</style>

            <div className="om-header">
                <h2>Orders Management</h2>
                <div className="om-header-actions">
                    <button onClick={loadAllData} className="om-refresh-btn">
                        Refresh Orders
                    </button>
                    <span className="om-total-count">
                        Total Orders: {orders.length}
                    </span>
                </div>
            </div>

            {/* Date Filter Section */}
            <div style={filterStyles.container}>
                <h3 style={filterStyles.title}>📅 Filter Orders by Date</h3>
                
                <div style={filterStyles.buttonGroup}>
                    <button
                        style={filterStyles.filterButton(dateFilter.filterType === 'today')}
                        onClick={() => handleFilterChange('today')}
                        className="filter-button"
                    >
                        Today
                    </button>
                    <button
                        style={filterStyles.filterButton(dateFilter.filterType === 'yesterday')}
                        onClick={() => handleFilterChange('yesterday')}
                        className="filter-button"
                    >
                        Yesterday
                    </button>
                    <button
                        style={filterStyles.filterButton(dateFilter.filterType === 'thisWeek')}
                        onClick={() => handleFilterChange('thisWeek')}
                        className="filter-button"
                    >
                        This Week
                    </button>
                    <button
                        style={filterStyles.filterButton(dateFilter.filterType === 'custom')}
                        onClick={() => handleFilterChange('custom')}
                        className="filter-button"
                    >
                        Custom Range
                    </button>
                </div>

                {dateFilter.filterType === 'custom' && (
                    <div style={filterStyles.dateRange}>
                        <input
                            type="date"
                            value={dateFilter.startDate}
                            onChange={(e) => setDateFilter(prev => ({ ...prev, startDate: e.target.value }))}
                            style={filterStyles.dateInput}
                        />
                        <span style={{ color: 'white' }}>to</span>
                        <input
                            type="date"
                            value={dateFilter.endDate}
                            onChange={(e) => setDateFilter(prev => ({ ...prev, endDate: e.target.value }))}
                            style={filterStyles.dateInput}
                        />
                    </div>
                )}

                {/* Summary Statistics */}
                <div style={filterStyles.summary}>
                    <div style={filterStyles.summaryItem}>
                        <div style={filterStyles.summaryLabel}>Orders in Period</div>
                        <div style={filterStyles.summaryValue}>{filteredOrders.length}</div>
                    </div>
                    <div style={filterStyles.summaryItem}>
                        <div style={filterStyles.summaryLabel}>Total Sales</div>
                        <div style={filterStyles.summaryValue}>{formatCurrency(totals.totalAmount)}</div>
                    </div>
                    <div style={filterStyles.summaryItem}>
                        <div style={filterStyles.summaryLabel}>Total Discount</div>
                        <div style={filterStyles.summaryValue}>{formatCurrency(totals.totalDiscount)}</div>
                    </div>
                    <div style={filterStyles.summaryItem}>
                        <div style={filterStyles.summaryLabel}>Final Total</div>
                        <div style={filterStyles.summaryValueHighlight}>{formatCurrency(totals.totalFinal)}</div>
                    </div>
                </div>
            </div>

            {filteredOrders.length === 0 ? (
                <div className="om-empty">
                    <p>No orders found for selected date range</p>
                    <button onClick={loadAllData} className="om-load-btn">
                        Load All Orders
                    </button>
                </div>
            ) : (
                <>
                    <div className="om-table-wrapper">
                        <table className="om-table">
                            <thead>
                                <tr>
                                    <th>Order #</th>
                                    <th>Items</th>
                                    <th>Total</th>
                                    <th>Discount</th>
                                    <th>Created</th>
                                    <th>Staff</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedOrders.map(order => (
                                    <tr key={order.id}>
                                        <td className="om-order-number">
                                            <strong>#{order.order_number}</strong>
                                        </td>
                                        <td className="om-items-preview">
                                            {order.items?.slice(0, 2).map((item, idx) => (
                                                <div key={idx} className="om-item-preview">
                                                    {item.item_name || "Unknown Item"} ×{item.quantity}
                                                </div>
                                            ))}
                                            {order.item_count > 2 && (
                                                <div className="om-more-items">
                                                    +{order.item_count - 2} more items
                                                </div>
                                            )}
                                            {order.item_count === 0 && (
                                                <div className="om-no-items">No items</div>
                                            )}
                                        </td>
                                        <td className="om-total">
                                            {formatCurrency(order.total_amount)}
                                        </td>
                                        <td className="om-discount">
                                            {formatCurrency(order.discount_amount)}
                                        </td>
                                        <td className="om-date">
                                            {formatDate(order.created_at)}
                                        </td>
                                        <td className="om-staff">
                                            {order.staff_name}
                                        </td>
                                        <td className="om-actions">
                                            <button 
                                                onClick={() => viewOrder(order)} 
                                                className="om-view-btn"
                                            >
                                                View Details
                                            </button>
                                            <button 
                                                onClick={() => deleteOrder(order)} 
                                                className="om-delete-btn"
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {totalPages > 1 && (
                        <div className="om-pagination">
                            <button 
                                disabled={currentPage === 1} 
                                onClick={() => setCurrentPage(p => p - 1)}
                            >
                                Previous
                            </button>
                            <span>Page {currentPage} of {totalPages}</span>
                            <button 
                                disabled={currentPage === totalPages} 
                                onClick={() => setCurrentPage(p => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}

            {showModal && selectedOrder && (
                <div className="om-modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="om-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="om-modal-header">
                            <h3>Order #{selectedOrder.order_number}</h3>
                            <button className="om-close-btn" onClick={() => setShowModal(false)}>
                                ×
                            </button>
                        </div>
                        
                        <div className="om-order-details">
                            <div className="om-detail-row">
                                <div className="om-detail-item">
                                    <strong>Staff:</strong>
                                    <span>{selectedOrder.staff_name}</span>
                                </div>
                                <div className="om-detail-item">
                                    <strong>Order Date:</strong>
                                    <span>{formatDate(selectedOrder.created_at)}</span>
                                </div>
                                <div className="om-detail-item">
                                    <strong>Total Items:</strong>
                                    <span>{selectedOrder.item_count}</span>
                                </div>
                            </div>
                            <div className="om-detail-row">
                                <div className="om-detail-item">
                                    <strong>Subtotal:</strong>
                                    <span>{formatCurrency(selectedOrder.total_amount)}</span>
                                </div>
                                <div className="om-detail-item">
                                    <strong>Discount:</strong>
                                    <span>{formatCurrency(selectedOrder.discount_amount)}</span>
                                </div>
                                <div className="om-detail-item total">
                                    <strong>Final Total:</strong>
                                    <span>{formatCurrency(
                                        Number(selectedOrder.total_amount) - Number(selectedOrder.discount_amount || 0)
                                    )}</span>
                                </div>
                            </div>
                        </div>

                        <div className="om-items-section">
                            <h4>Order Items</h4>
                            {selectedOrder.items?.length > 0 ? (
                                <table className="om-items-table">
                                    <thead>
                                        <tr>
                                            <th>Item Name</th>
                                            <th>Quantity</th>
                                            <th>Unit Price</th>
                                            <th>Subtotal</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedOrder.items.map((item, i) => (
                                            <tr key={i}>
                                                <td>{item.item_name || "Unknown Item"}</td>
                                                <td>{item.quantity}</td>
                                                <td>{formatCurrency(item.price)}</td>
                                                <td>{formatCurrency(item.subtotal)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div className="om-no-items-modal">
                                    No items found for this order
                                </div>
                            )}
                        </div>
                        
                        <div className="om-modal-footer">
                            <button 
                                className="om-close-modal-btn" 
                                onClick={() => setShowModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}